import { useState } from "react";

import { useAuth } from "../Authentification/AuthContext";
import { createAuthFetch } from "../Authentification/apiFetch";

type ContentValue =
    | "Information"
    | "Entertainment"
    | "Identity"
    | "Social";

interface PostAnalysis {
    scores: {
        hook: number;
        readability: number;
        structure: number;
        authenticity: number;
        linkedin_fit: number;
        call_to_action: number;
    };
    contentValue: ContentValue[];
    overall_score: number;
    feedback: [string, string, string];
}

interface TextAnalysisProps {
    postText: string;
}

type AuthFetch = (
    url: string,
    options?: RequestInit
) => Promise<Response>;

async function fetchAnalysis(
    freeText: string,
    apiFetch: AuthFetch
): Promise<PostAnalysis> {
    const response = await apiFetch(
        "/api/analyse-post",
        {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                text: freeText,
            }),
        }
    );

    if (!response.ok) {
        const data = await response
            .json()
            .catch(() => null);

        throw new Error(
            data?.details ??
            data?.error ??
            "Fehler bei der Post-Analyse"
        );
    }

    const responseData = await response.json();

    const data = JSON.parse(
        responseData.post
    );

    return data;
}

export default function TextAnalysis({
    postText,
}: TextAnalysisProps) {
    const { token } = useAuth();

    const apiFetch =
        createAuthFetch(token);

    const [data, setData] =
        useState<PostAnalysis>();

    const [isLoading, setIsLoading] =
        useState(false);

    const [error, setError] =
        useState<string | null>(null);

    const handleGenerate = async () => {
        setIsLoading(true);
        setError(null);

        try {
            const result =
                await fetchAnalysis(
                    postText,
                    apiFetch
                );

            console.log(
                "API response:",
                result
            );

            setData(result);
        } catch (err) {
            console.error(err);

            setError(
                err instanceof Error
                    ? err.message
                    : "Fehler bei der Analyse"
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div>
            <button
                onClick={handleGenerate}
                disabled={isLoading}
            >
                {isLoading
                    ? "Analysiere..."
                    : "Analysiere Post"}
            </button>

            {error && (
                <p>
                    {error}
                </p>
            )}

            {data && (
                <div>
                    <p>
                        Overall Score:{" "}
                        {data.overall_score}/100
                    </p>

                    <p>Scores</p>

                    <ul>
                        <li>
                            Hook:{" "}
                            {data.scores.hook}/10
                        </li>

                        <li>
                            Lesbarkeit:{" "}
                            {
                                data.scores
                                    .readability
                            }
                            /10
                        </li>

                        <li>
                            Struktur:{" "}
                            {
                                data.scores
                                    .structure
                            }
                            /10
                        </li>

                        <li>
                            Authentizität:{" "}
                            {
                                data.scores
                                    .authenticity
                            }
                            /10
                        </li>

                        <li>
                            LinkedIn Fit:{" "}
                            {
                                data.scores
                                    .linkedin_fit
                            }
                            /10
                        </li>

                        <li>
                            Call to Action:{" "}
                            {
                                data.scores
                                    .call_to_action
                            }
                            /10
                        </li>
                    </ul>

                    <p>Feedback</p>

                    <ul>
                        {data.feedback.map(
                            (
                                feedback,
                                index
                            ) => (
                                <li
                                    key={index}
                                >
                                    {feedback}
                                </li>
                            )
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}